/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {MouseEventHandler} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import ChevronRight from '@material-ui/icons/ChevronRight';
import {dlsColor} from '@ion/dls/src/theme/theme';

export interface IconCheckboxLabelProps {
  label: string | JSX.Element;
  onIconClick?: MouseEventHandler<HTMLDivElement>;
  icon?: JSX.Element;
}

const useStyles = makeStyles(theme => ({
  checkbox : {
    display : 'flex',
    padding : '5px 0px'
  },
  checkboxLabel : {
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center'
  },
  checkboxChevronContainer : {
    display    : 'flex',
    alignItems : 'center'
  },
  checkboxChevron : {
    fill       : dlsColor('grey3'),
    transition : 'transform 300ms ease-in-out',
    '&:hover'  : {
      transform  : 'translateX(3px)',
      transition : 'transform 300ms ease-in-out'
    }
  }
}));

/**
 * @IconCheckboxLabel
 *
 * Abstracted Checkbox Label that allow for a user to pass
 * a clickable icon as the label.
 *
 * ```typescript
 * import {IconCheckboxLabel} from '@ion/dls/src/components/checkbox';
 *
 * <IconCheckboxLabel label='Choose me'
 *   onIconClick={handleIconClick}
 *   icon={<NewIcon />} />
 *
 * ```
 *
 * @param {string} label: checkbox label
 * @param {Function} onIconClick: callback for when icon is clicked
 * @param {JSX.Element} icon: overrides default chevron as checkbox labels icon
 * @returns {ReactNode}
 */
export const IconCheckboxLabel = ({
  label,
  onIconClick,
  icon
}: IconCheckboxLabelProps) => {
  const classes = useStyles();

  return (
    <div className={classes.checkbox}>
      <span className={classes.checkboxLabel}>{label}</span>
      {
        onIconClick &&
          <div className={classes.checkboxChevronContainer}
            onClick={onIconClick}>
            {icon ?? <ChevronRight className={classes.checkboxChevron} />}
          </div>
      }
    </div>
  );
};

export default IconCheckboxLabel;
